import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

export type ModalTransitionContextValue = {
  /**
   * Increments the count of native modal dismissals in progress.
   * Each call must be paired with `notifyDismissCompleted`.
   */
  notifyDismissStarted: () => void;
  /**
   * Decrements the dismiss-in-flight count and runs any work queued by
   * `runAfterIdle` when the count reaches zero.
   */
  notifyDismissCompleted: () => void;
  /**
   * Runs `fn` after the current React commit cycle. If any coordinated dismiss
   * is in flight, `fn` is queued until all dismissals have completed.
   */
  runAfterIdle: (fn: () => void) => void;
  /** True while at least one coordinated native dismiss has started and not completed. */
  isDismissInFlight: () => boolean;
};

const ModalTransitionContext =
  createContext<ModalTransitionContextValue | null>(null);

const noop = () => undefined;

const defaultModalTransitionValue: ModalTransitionContextValue = {
  notifyDismissStarted: noop,
  notifyDismissCompleted: noop,
  runAfterIdle: (fn) => {
    setTimeout(fn, 0);
  },
  isDismissInFlight: () => false,
};

/**
 * Coordinates React Native `Modal` present/dismiss so a second modal is not
 * shown while another is still dismissing (iOS / transparent modals).
 *
 * @see https://github.com/facebook/react-native/issues/29929
 */
export const ModalTransitionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dismissDepthRef = useRef(0);
  const queueRef = useRef<Array<() => void>>([]);

  const flushQueue = useCallback(() => {
    if (dismissDepthRef.current !== 0) {
      return;
    }
    const pending = [...queueRef.current];
    queueRef.current = [];
    pending.forEach((fn) => {
      fn();
    });
  }, []);

  const notifyDismissCompleted = useCallback(() => {
    dismissDepthRef.current = Math.max(0, dismissDepthRef.current - 1);
    flushQueue();
  }, [flushQueue]);

  const notifyDismissStarted = useCallback(() => {
    dismissDepthRef.current += 1;
  }, []);

  const runAfterIdle = useCallback((fn: () => void) => {
    const schedule = () => {
      if (dismissDepthRef.current === 0) {
        fn();
      } else {
        queueRef.current.push(fn);
      }
    };
    setTimeout(schedule, 0);
  }, []);

  const isDismissInFlight = useCallback(() => dismissDepthRef.current > 0, []);

  const value = useMemo(
    () => ({
      notifyDismissStarted,
      notifyDismissCompleted,
      runAfterIdle,
      isDismissInFlight,
    }),
    [
      notifyDismissStarted,
      notifyDismissCompleted,
      runAfterIdle,
      isDismissInFlight,
    ]
  );

  return (
    <ModalTransitionContext.Provider value={value}>
      {children}
    </ModalTransitionContext.Provider>
  );
};

/**
 * Modal transition coordination. At callsites, prefer `runAfterIdle`; `notifyDismissStarted`
 * / `notifyDismissCompleted` are low-level and used by `useCoordinatedNativeModal`.
 *
 * When wrapped in `ModalTransitionProvider`, coordinates native dismiss depth and queued work.
 * Otherwise a safe default (no dismiss pairing; `runAfterIdle` deferred one macrotask).
 */
export const useModalTransition = (): ModalTransitionContextValue => {
  const ctx = useContext(ModalTransitionContext);
  return ctx ?? defaultModalTransitionValue;
};
